/**
 * Refcounted Firestore snapshot cache for useSyncExternalStore.
 * One attach() per key while any React consumer is subscribed.
 */

export interface SubscriptionSnapshot<T> {
  data: T;
  loading: boolean;
}

export interface SharedSubscription<T> {
  subscribe: (key: string, listener: () => void) => () => void;
  getSnapshot: (key: string) => SubscriptionSnapshot<T>;
}

interface SharedSubscriptionOptions<T> {
  getDefault: (key: string) => T;
  attach: (key: string, setSnapshot: (snapshot: SubscriptionSnapshot<T>) => void) => () => void;
}

export function createSharedSubscription<T>(
  options: SharedSubscriptionOptions<T>,
): SharedSubscription<T> {
  const entries = new Map<string, SubscriptionSnapshot<T>>();
  // Per-key default-snapshot cache. Lives independently of `entries` so that
  // getSnapshot(key) returns the same reference on repeat calls even when no
  // subscriber is attached yet. React's useSyncExternalStore may call
  // getSnapshot before subscribe, and across renders, and treats new object
  // references as state changes (=> render loop). The cache is cleared when
  // the last subscriber for a key leaves so a later re-subscribe starts fresh.
  const defaults = new Map<string, SubscriptionSnapshot<T>>();
  const listeners = new Map<string, Set<() => void>>();
  const refcounts = new Map<string, number>();
  const unsubs = new Map<string, () => void>();

  function defaultSnapshot(key: string): SubscriptionSnapshot<T> {
    let snap = defaults.get(key);
    if (!snap) {
      snap = { data: options.getDefault(key), loading: true };
      defaults.set(key, snap);
    }
    return snap;
  }

  function getSnapshot(key: string): SubscriptionSnapshot<T> {
    return entries.get(key) ?? defaultSnapshot(key);
  }

  function notify(key: string): void {
    listeners.get(key)?.forEach((listener) => listener());
  }

  function subscribe(key: string, listener: () => void): () => void {
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key)!.add(listener);

    const nextRef = (refcounts.get(key) ?? 0) + 1;
    refcounts.set(key, nextRef);

    if (nextRef === 1) {
      entries.set(key, defaultSnapshot(key));
      const unsub = options.attach(key, (snapshot) => {
        entries.set(key, snapshot);
        notify(key);
      });
      unsubs.set(key, unsub);
    }

    return () => {
      listeners.get(key)?.delete(listener);
      const remaining = (refcounts.get(key) ?? 1) - 1;
      if (remaining <= 0) {
        refcounts.delete(key);
        unsubs.get(key)?.();
        unsubs.delete(key);
        entries.delete(key);
        defaults.delete(key);
        listeners.delete(key);
      } else {
        refcounts.set(key, remaining);
      }
    };
  }

  return {
    subscribe,
    getSnapshot,
  };
}
